import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import fuzzysort from 'fuzzysort';
import { motion } from 'framer-motion';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '../styled';
import { useWrappedPopOver } from '../context-menu';
const SelectContainerElem = styled("div", ({ fullWidth }) => ({
    width: fullWidth === true ? "100%" : "min(300px, calc(100% - 20px))",
    fontSize: 14,
    fontFamily: 'hiyllo, sans-serif',
}));
const SelectInputElement = styled("input", ({ $theme }) => ({
    height: 20,
    width: "100%",
    margin: -5,
    padding: 5,
    paddingLeft: 7.5,
    paddingRight: 7.5,
    background: $theme.background3,
    borderRadius: 5,
    border: "none",
    outline: "none",
    color: $theme.foreground,
}));
const StaticContainerElem = styled(motion.div, ({ $theme }) => ({
    background: $theme.midground,
    color: $theme.foreground,
    borderRadius: 10,
    padding: "10px 0.8em",
    height: 20,
    display: "flex",
    flexDirection: "row",
    gap: 10,
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    transition: "box-shadow cubic-bezier(0.1, 0.12, 0.1, 0.95) 0.3s",
    ":hover": {
        boxShadow: "0px 0px 15px 5px rgba(0, 0, 0, 0.5)"
    },
}));
const OptionContainerElem = styled(motion.div, ({ $theme, $hovered, $highlight }) => ({
    background: $highlight ? $theme.background3 : $hovered ? $theme.midground1Highlight : $theme.midground1,
    color: $theme.foreground,
    padding: "10px 0.8em",
    height: 20,
    display: "flex",
    flexDirection: "row",
    gap: 5,
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
}));
const SelectExpandedContainer = styled(motion.div, ({ $theme, width }) => ({
    width,
    background: $theme.background1,
    color: $theme.foreground,
    borderRadius: 10,
    minWidth: 100,
    display: "flex",
    flexDirection: "column",
    fontSize: 14,
    overflow: "hidden",
    maxHeight: 300,
    overflowY: "auto",
    boxShadow: "rgba(0,0,0,0.25) 0px 0px 20px 0px",
}));
const OptionContainer = React.memo(function OptionContainer(props) {
    const ref = React.useRef(null);
    const [hovered, setHovered] = React.useState(false);
    React.useEffect(() => {
        if (props.active === true && ref.current != null) {
            ref.current.scrollIntoView({
                block: "nearest",
                inline: "nearest",
            });
        }
    }, [props.active]);
    return (_jsx(OptionContainerElem, { _ref: ref, ...props, "$hovered": hovered, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }));
});
export function idleCallbackSafe(cb) {
    if (window.requestIdleCallback) {
        window.requestIdleCallback(cb);
    }
    else {
        setTimeout(cb, 0);
    }
}
export const SelectWeb = React.memo(function SelectWeb(props) {
    const staticContainerRef = React.useRef(null);
    const [staticDimensions, setStaticDimensions] = React.useState({
        width: 0,
        height: 0,
    });
    const selectedOption = props.options.find((o) => o.value === props.value);
    const [query, setQuery] = React.useState("");
    const [highlightedIndex, setHighlightedIndex] = React.useState(null);
    React.useEffect(() => {
        if (staticContainerRef.current != null) {
            setStaticDimensions({
                height: staticContainerRef.current.clientHeight,
                width: staticContainerRef.current.clientWidth,
            });
        }
    }, []);
    const { open: _open, close, CXMenuContainer, ref, } = useWrappedPopOver({
        offset: {
            x: 0,
            y: 0,
        },
        displayMode: "fixed",
    });
    const open = React.useCallback(() => {
        setHighlightedIndex(null);
        _open();
    }, [_open]);
    const onClickOption = React.useCallback((option) => {
        idleCallbackSafe(() => {
            if (props.onChangeValue != null) {
                props.onChangeValue(option.value);
            }
        });
        close();
    }, [props, close]);
    const filteredOptions = React.useMemo(() => {
        if (query.length === 0) {
            return props.options;
        }
        const fsResults = fuzzysort.go(query, props.options, {
            keys: ["label", "alternateLabel"],
        });
        return fsResults.map((r) => r.obj);
    }, [props.options, query]);
    const onClickStatic = React.useCallback(() => {
        if (props.disabled !== true) {
            open();
        }
    }, [props.disabled, open]);
    const onKeyUp = React.useCallback((evt) => {
        if (evt.key === "Enter") {
            if (filteredOptions.length > 0) {
                onClickOption(filteredOptions[highlightedIndex ?? 0]);
            }
            else {
                props.onCustomValueChosen?.(query);
            }
            close();
        }
        if (evt.key === "ArrowDown") {
            setHighlightedIndex((prev) => {
                if (prev == null) {
                    prev =
                        filteredOptions.findIndex((o) => o.value === props.value) ?? 0;
                }
                return prev === filteredOptions.length - 1 ? 0 : prev + 1;
            });
        }
        if (evt.key === "ArrowUp") {
            setHighlightedIndex((prev) => {
                if (prev == null) {
                    prev =
                        filteredOptions.findIndex((o) => o.value === props.value) ?? 0;
                }
                return prev === 0 ? filteredOptions.length - 1 : prev - 1;
            });
        }
        console.log(evt.key);
    }, [close, filteredOptions, highlightedIndex, onClickOption, props.value]);
    return (_jsxs(SelectContainerElem, { fullWidth: props.fullWidth, style: props.containerStyle, _ref: ref, children: [props.customStaticElement != null ? (_jsx("div", { onClick: onClickStatic, ref: staticContainerRef, children: props.customStaticElement })) : (_jsxs(StaticContainerElem, { onClick: onClickStatic, _ref: staticContainerRef, children: [selectedOption?.icon ? (_jsx(FontAwesomeIcon, { icon: selectedOption.icon, fixedWidth: true })) : (_jsx("div", { style: { paddingLeft: 2.5 }, children: selectedOption?.label ?? props.placeholder })), props.disabled ? null : _jsx(FontAwesomeIcon, { icon: faCaretDown })] })), _jsx(CXMenuContainer, { withAnimatePresence: true, style: {
                    boxShadow: "rgba(0, 0, 0, 0.5) 0px 0px 20px 0px"
                }, children: _jsxs(SelectExpandedContainer, { width: staticDimensions.width, initial: { height: staticDimensions.height }, animate: { height: "" }, exit: { height: staticDimensions.height }, transition: { duration: 0.1 }, style: { ...(props.expandedContainerStyle ?? {}), minHeight: staticDimensions.height, maxHeight: "inherit" }, children: [_jsx(OptionContainer, { children: _jsx(SelectInputElement, { autoFocus: true, onChange: (evt) => {
                                    setQuery(evt.currentTarget.value);
                                    setHighlightedIndex(0);
                                }, onKeyUp: onKeyUp, value: query }) }), _jsx("div", { style: {
                                height: "100%",
                                overflowY: "auto",
                            }, children: filteredOptions.map((option, index) => (_jsxs(OptionContainer, { onClick: () => onClickOption(option), "$highlight": query.length > 0
                                    ? index === (highlightedIndex ?? 0)
                                    : highlightedIndex == null
                                        ? option.value === props.value
                                        : index === highlightedIndex, active: option.value === props.value, children: [option.icon ? _jsx(FontAwesomeIcon, { icon: option.icon }) : null, _jsx("div", { style: { paddingLeft: 2.5 }, children: option.label })] }, option.label))) })] }) })] }));
});
